import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { License } from "@/Common/Api/Employee/Dto/License";
import { RamqGl6Info } from "@/Common/Api/Ramq/RamqGl6Info";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { Person } from "@/Common/Models/Person/Person";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { EmployeeTitle } from "../Employee/Enums/EmployeeTitle";
import { EmployeeLight } from "../UI/Employee/EmployeeLight";
import { RAMQDispensateurClass } from "./enums/RAMQDispensateurClass";
import { RAMQInscription } from "./RAMQInscription";

enum RamqStatus {
    Undefined = -1,
    CrashWhileLoadingCachedInscriptionData = 0,
    SubscribedInClinic = 1, // success
    SubscribedWithoutVisit = 2,
    SubscribedOutsideClinic = 3, // other clinic
    NotSubscribed = 4,
    NoInformation_UnableToSearchRamq = 5,
    NoNAM = 10,
    InvalidNAM = 11, // NamDoesNotExistAtRamq
    OtherError = 12,
    InvalidSearchParameters = 13,
    NoInscriptionFoundInCache = 14,
    RamqIsDisabledForEnvironnement = 15,
    Registered_WithDoctorDetail = 16,

    /// <summary>[API] The patient is registered with a group of doctors which may be in another clinic. The GL6 service always provide the clinic's name associated to the patient for that group.
    RegisteredInGroup = 18
}

class RAMQInfo {
    public AsyncSenderID: string;
    public QuestionCode: number;
    public Note: string;
    public CodeV: string;
    public Result: RamqStatus;
    public Errors: any[];
    public FirstName: string;
    public LastName: string;

    // info from Ramq B2B service - All inscription info
    public RAMQInscription: RAMQInscription;

    // info from Ramq GL6 service - All group inscription and doctor info
    public RamqGl6Info: RamqGl6Info;

    public FromResponse(response: ServerEvent): RAMQInfo {
        if (!response)
            return this;

        this.FromResponseData(response.Data);
        this.Errors = response.Errors;

        return this;
    }

    public FromResponseData(data: any): RAMQInfo {
        if (!data)
            return this;

        this.QuestionCode = data.QuestionCode;
        this.Note = data.Note;
        this.CodeV = data.CodeV;
        this.Result = data.Status;
        this.Errors = [];
        this.FirstName = data.FirstName;
        this.LastName = data.LastName;
        this.RAMQInscription = data.RAMQInscription;
        this.RamqGl6Info = data.RamqGl6Info;

        return this;
    }

    /**
     * Resolves the active professional associated with the license number and or doctor class.
     * @param {string} doctorClass - the class of the professional E.G.: Doctor, IPS etc
     * @param {string} licence - The license number used to resolve clinician.
     * @returns {Person} The active clinician associated with the license number if found; otherwise, null.
     */
    public static activeProfessional(doctorClass: string, license: string): Person | null {
        return (doctorClass === RAMQDispensateurClass.IPS)
            ? RAMQInfo.ResolveActiveClinicianByRamqNumber(`${doctorClass}${license}`)
            : RAMQInfo.ResolveActiveClinician(license) ?? RAMQInfo.ResolveActiveClinicianByRamqNumber(`${doctorClass}${license}`);
    }

    /**
     * Resolves clinicians based on the provided license number.
     * @param {string} licence - The license number used to resolve clinicians.
     * @returns {Person[]} An array of clinicians matching the provided license number.
     */
    public static ResolveClinicians(licence: string): Person[] {
        if (IsNullOrWhiteSpace(licence))
            return [];

        return cacheStore.CliniciansWithInactive.filter(p => p.EmployeeModel.Licences?.some(license => license.LicenceNumber === licence) === true);
    }

    /**
     * Resolves the active clinician associated with the license number.
     * @param {string} licence - The license number used to resolve clinician.
     * @returns {Person} The active clinician associated with the license number if found; otherwise, null.
     */
    public static ResolveActiveClinician(licence: string): Person | null {
        return RAMQInfo.ResolveClinicians(licence).firstOrDefault(p => !p.IsInactive);
    }

    /**
     * Resolves the name of the clinician associated with the provided license number.
     * @param {string} licence - The license number of the clinician.
     * @returns {string} The name of the clinician associated with the provided license number, or an empty string if not found.
     */
    public static ResolveClinicianName(licence: string): string {
        return RAMQInfo.ResolveActiveClinician(licence)?.NameWithPrefix
            ?? "";
    }

    /**
     * Resolves clinicians based on the provided RAMQ number.
     * @param {string} ramqNumber - The RAMQ number used to resolve clinicians.
     * @returns {Person[]} An array of clinicians matching the provided RAMQ number.
     */
    public static ResolveCliniciansByRamqNumber(ramqNumber: string): Person[] {
        if (IsNullOrWhiteSpace(ramqNumber))
            return [];

        return cacheStore.CliniciansWithInactive
            .filter(p =>
                p.EmployeeModel.RamqNumber?.startsWith(ramqNumber) === true
                && (p.EmployeeModel.RamqNumber?.length === 6
                || p.EmployeeModel.RamqNumber?.length === 7)
            ); // stored RAMQ numbers often have additional info at the end
    }

    /**
     * Resolves the active clinician associated with the given RAMQ number.
     * @param {string} ramqNumber - The RAMQ number of the clinician.
     * @returns {Person} The active clinician associated with the RAMQ number, if found; otherwise, null.
     */
    public static ResolveActiveClinicianByRamqNumber(ramqNumber: string): Person | null {
        return RAMQInfo.ResolveCliniciansByRamqNumber(ramqNumber)?.firstOrDefault(p => !p.EmployeeModel?.IsInactive);
    }

    /**
     * Resolves the name of the clinician associated with the provided license number.
     * @param {string} ramqNumber - The RAMQ number of the clinician.
     * @returns {string} The name of the clinician associated with the provided license number, or an empty string if not found.
     */
    public static ResolveClinicianNameByRamqNumber(ramqNumber: string): string {
        return RAMQInfo.ResolveActiveClinicianByRamqNumber(ramqNumber)?.NameWithPrefix ?? "";
    }

    /**
     * Resolves the name of the provider based on RAMQ inscription information.
     * @param {RAMQInscription} ramqInfo - The RAMQ inscription information of the provider.
     * @returns {string} The name of the provider resolved from the RAMQ information.
     */
    public static ResolveProviderName(ramqInfo: RAMQInscription): string {
        const providerName = ramqInfo.DoctorClass === RAMQDispensateurClass.IPS
            ? RAMQInfo.ResolveClinicianNameByRamqNumber(`${ramqInfo.DoctorClass}${ramqInfo.DoctorLicence}`)
            : RAMQInfo.ResolveClinicianName(ramqInfo.DoctorLicence);

        return !IsNullOrWhiteSpace(providerName)
            ? providerName
            : `${ramqInfo.DoctorClass === RAMQDispensateurClass.IPS ? ramqInfo.DoctorClass : "" }${ramqInfo.DoctorLicence}`;
    }

    /**
     * Extracts the 5-digit license number based on the RAMQ status.
     * @returns {string} The 5-digit license number extracted based on the RAMQ status, or an empty string if not applicable.
     */
    public Extract5DigitLicenceNumber(): string {
        if (this.Result == RamqStatus.SubscribedInClinic || this.Result == RamqStatus.SubscribedWithoutVisit)
            return this.Note ?? "";

        if (this.Result == RamqStatus.Registered_WithDoctorDetail)
            return this.RamqGl6Info?.Doctor.Licence ?? "";

        return "";
    }

    /**
     * Retrieves the dispensateur number from the given RAMQ number, if it's a valid IPS RAMQ number.
     * @param {string} ramqNumber - The RAMQ number to extract the dispensateur number from.
     * @returns {string} The dispensateur number extracted from the RAMQ number, or an empty string if the RAMQ number is invalid.
     */
    public static GetExtractDispensateurNumber(ramqNumber: string): string {
        return (RAMQInfo.ValidateIPSRamqNumber(ramqNumber))
            ? ramqNumber.substring(1, 6)
            : "";
    }

    /**
     * Resolves the dispensateur class for the given person based on their role.
     * @param {Person} person - The person for whom to resolve the dispensateur class.
     * @returns {RAMQDispensateurClass|null} The dispensateur class resolved for the person, or null if the person is invalid or the class cannot be determined.
     */
    public static ResolveDispensableClass(employee: Employee): RAMQDispensateurClass {
        if (!employee)
            return null;

        if (EmployeeTitleHelper.isDoctor(Employee.getTitle(employee)))
            return RAMQDispensateurClass.Doctor;

        if (Employee.getIssuingOrgRule(employee)?.Title === EmployeeTitle.NursePractitioner)
            return RAMQDispensateurClass.IPS;

        return null;
    }

    /**
     * Resolves the RAMQ number for the given employee.
     * @param {Person} person - The employee to resolve the RAMQ number for.
     * @returns {string} The RAMQ number resolved for the employee, or an empty string if the RAMQ number is invalid.
     */
    public static ResolveRamqNumber(employee: Employee): string {
        const dispensateurClass = this.ResolveDispensableClass(employee);

        let ramqNumber = employee.RamqNumber;
        let ramqNumberWithChecksum = this.appendRamqCheckSumIfMissing(ramqNumber, dispensateurClass);

        if (ramqNumberWithChecksum.length === 7) return ramqNumberWithChecksum;

        const licence = employee.Licences.firstOrDefault();
        const licenceNumber = licence.LicenceNumber;
        const licenceOrganization = cacheStore.AllLicenseOrganizationRulesById[licence.IssuingLicenseOrganizationRuleId]?.OrganisationCode;

        if (
            licenceOrganization !== "CMQ" ||
            !((licenceNumber.length === 6 && licenceNumber.toLowerCase().startsWith("r")) ||
              (licenceNumber.length === 5))
        ) return "";

        ramqNumber = licenceNumber.toLowerCase().startsWith("r")
            ? `5${licenceNumber.slice(1)}`
            : `1${licenceNumber}`;
        ramqNumberWithChecksum = this.appendRamqCheckSumIfMissing(ramqNumber, dispensateurClass);

        if (ramqNumberWithChecksum.length === 7) return ramqNumberWithChecksum;

        return "";
    }

    /**
     * Validates whether the given RAMQ number is a valid IPS RAMQ number.
     * @param {string} ramqNumber - The RAMQ number to validate.
     * @returns {boolean} True if the RAMQ number is a valid IPS RAMQ number, otherwise false.
     */
    public static ValidateIPSRamqNumber(ramqNumber: string): boolean {
        if (IsNullOrWhiteSpace(ramqNumber))
            return false;

        return (ramqNumber.startsWith("8") && (ramqNumber.length === 6 || ramqNumber.length === 7));
    }

    /**
     * Validates whether the given license number of Doctor license.
     * @param {License} license - The license.
     * @returns {boolean} True if the license number is a valid doctor, otherwise false.
     */
    public static IsValidDoctorLicence(license: License): boolean {
        return license.LicenceNumber.length === 5;
    }

    /**
     * Splits and formats the RAMQ number into a standardized format.
     * @param {string} ramqNumber - The RAMQ number to split and format.
     * @returns {string} The RAMQ number formatted into a standardized format.
     */
    public static SplitAndFormatRamqNumber(ramqNumber: string): string {
        let formattedLicenceOrRamqNumber = null;
        if (ramqNumber.length == 7)
            formattedLicenceOrRamqNumber = `${ramqNumber[0]}-${ramqNumber.substring(1, 6)}-${ramqNumber[6]}`;
        else
            formattedLicenceOrRamqNumber = ramqNumber;

        return formattedLicenceOrRamqNumber;
    }

    /**
     * Retrieves the current resident based on the provided medical reference license.
     * @param {string} licenceMedecinReferant - The medical reference license of the resident.
     * @returns {Person|null} The current resident, if found; otherwise, null.
     */
    public static GetCurrentResident(licenceMedecinReferant: string): Person {
        return cacheStore.GetClinicianByLicence(licenceMedecinReferant) ?? cacheStore.GetClinicianByRamqNumber(`${RAMQDispensateurClass.Doctor}${licenceMedecinReferant}`);
    }

    /**
     * Retrieves the formatted name of the group registration clinic.
     * @returns {string} The formatted name of the group registration clinic.
     */
    public getGroupRegistrationClinicName(): string {
        return (this.RamqGl6Info?.GmfRegistration?.Name
            ?.replace(/clinique/gi, "clin.")
            ?.replace(/clinic/gi, "clin.")
            ?.replace(/medicale/gi, "med.")
            ?.replace(/médicale/gi, "med.")
            ?.replace(/medical/gi, "med.")
            ?.replace(/sainte/gi, "ste")
            ?.replace(/saint/gi, "st")
            ?? ""
        ).toTitleCase();
    }

    /**
     * Retrieves the formatted name of the GL6 doctor's clinic.
     * @returns {string} The formatted name of the GL6 doctor's clinic.
     */
    public getGl6DoctorClinicName(): string {
        return (this.RamqGl6Info?.Doctor?.LocationName
            ?.replace(/clinique/gi, "clin.")
            ?.replace(/clinic/gi, "clin.")
            ?.replace(/medicale/gi, "med.")
            ?.replace(/médicale/gi, "med.")
            ?.replace(/medical/gi, "med.")
            ?.replace(/sainte/gi, "ste")
            ?.replace(/saint/gi, "st")
            ?? ""
        ).toTitleCase();
    }

    /**
     * Retrieves the name of the GL6 service doctor in the format "Dr Lastname, Firstname".
     * @returns {string} The name of the GL6 service doctor.
     */
    public getGL6ServiceDoctorName(): string {
        return `${this.getGL6DoctorLicenseType()} ${this.RamqGl6Info?.Doctor?.LastName}, ${this.RamqGl6Info?.Doctor?.FirstName}`;
    }

    private getGL6DoctorLicenseType(): string {
        switch (this.RamqGl6Info?.Doctor?.LicenseType) {
            case 1:
            case 6:
                return "Dr";

            case 8:
                return "IPS";

            default:
                return "";
        }
    }

    /**
     * Retrieves the RAMQ provider name based on the current object's RAMQ information.
     * @returns {string} The RAMQ provider name resolved from the current object's RAMQ information.
     */
    public getRamqProviderName(): string {
        if (this.RAMQInscription) {
            const ramqinfo = this.RAMQInscription;
            ramqinfo.DoctorLicence = this.Extract5DigitLicenceNumber();
            let providerName = RAMQInfo.ResolveProviderName(ramqinfo);

            if (!IsNullOrWhiteSpace(providerName)) {
                if (cacheStore.ClinicSettings.EnableIpsEnrolmentForRamq && this.RAMQInscription.DoctorClass === RAMQDispensateurClass.IPS)
                    providerName = `IPS: ${providerName}`;
                return providerName;
            }
        }
        if (!IsNullOrWhiteSpace(this.RamqGl6Info?.Doctor?.LastName))
            return this.getGL6ServiceDoctorName();

        if (!IsNullOrWhiteSpace(this.Extract5DigitLicenceNumber()))
            return this.Extract5DigitLicenceNumber();

        return "";
    }

    /**
     * Retrieves the RAMQ provider based on the current object's RAMQ information.
     * @returns {EmployeeLight | null} The RAMQ provider resolved from the current object's RAMQ information.
     */
    public getRamqProvider(): EmployeeLight | null {
        const doctorLicence = this.Extract5DigitLicenceNumber();
        const contactFacetId = RAMQInfo.ResolveActiveClinician(doctorLicence)?.ContactFacetId;

        if (this.RAMQInscription) {
            let providerName = RAMQInfo.ResolveProviderName(this.RAMQInscription);

            if (!IsNullOrWhiteSpace(providerName) && cacheStore.ClinicSettings.EnableIpsEnrolmentForRamq && this.RAMQInscription.DoctorClass === RAMQDispensateurClass.IPS) {
                providerName = `IPS: ${providerName}`;
            }

            if (!IsNullOrWhiteSpace(providerName)) {
                return new EmployeeLight(contactFacetId, providerName, doctorLicence);
            }
        }

        if (this.RamqGl6Info?.Doctor?.LastName) {
            return new EmployeeLight(contactFacetId, this.getGL6ServiceDoctorName(), doctorLicence);
        }

        return null;
    }

    /**
     * Checks if the current object has a RAMQ doctor status.
     * @returns {boolean} True if the object has a RAMQ doctor status, otherwise false.
     */
    public HasRamqDoctorStatus(): boolean {
        return this.Result == RamqStatus.SubscribedInClinic
            || this.Result == RamqStatus.SubscribedWithoutVisit
            || this.Result == RamqStatus.Registered_WithDoctorDetail;
    }

    /**
     * Retrieves formatted RAMQ information based on the current object's status.
     * @returns {string} Formatted RAMQ information based on the current object's status.
     */
    public getFormattedRamqInfoByStatus(): string {
        switch (this.Result) {
            case RamqStatus.SubscribedInClinic:
                return `${this.GetFormattedRamqIntevenor()}`;

            case RamqStatus.SubscribedWithoutVisit:
                return `[SV] ${this.GetFormattedRamqIntevenor()}`;

            case RamqStatus.NotSubscribed:
                return cacheStore.GetString("NotEnrolledLabel");

            case RamqStatus.InvalidNAM:
                return `${cacheStore.GetString("RamqSubscribtionStatus_NAMInValid")}`;

            // maybe enrolled outside clinic
            case RamqStatus.RegisteredInGroup:
                return `${cacheStore.GetString("RamqAssociatedLabel")} ` + this.getGroupRegistrationClinicName();

            // maybe enrolled outside clinic
            case RamqStatus.Registered_WithDoctorDetail:
                return `${this.GetFormattedRamqIntevenor()} | ${this.getGl6DoctorClinicName()}`;

            // deprecated (GL6 will always give us the doctor name now)
            case RamqStatus.SubscribedOutsideClinic:
                return cacheStore.GetString("EnrolledInAnotherClinicLabel");

            default:
                return this.Errors && this.Errors.length > 0
                    ? this.Errors[0].Message
                    : cacheStore.GetString("NoInfoRamqLabel");
        }
    }

    /**
     * Retrieves formatted RAMQ CV information.
     * @returns {string} Formatted RAMQ CV information.
     */
    public getFormattedRamqCV(): string {
        return this.CodeV || cacheStore.GetString("RamqNoCodeVLabel");
    }

    /**
     * Generate and appends the RAMQ checksum digit if missing from the partial RAMQ number.
     * @param {string} partialRamqNumber - The partial RAMQ number (without the checksum).
     * @param {RAMQDispensateurClass} dispensateurClass - The dispensateur class to use if the partial RAMQ number is 5 digits long. Default is RAMQDispensateurClass.Doctor.
     * @returns {string} The partial RAMQ number with the checksum appended, or the original partial RAMQ number if it's invalid or already has the checksum.
     */
    public static appendRamqCheckSumIfMissing(partialRamqNumber: string, dispensateurClass: RAMQDispensateurClass = RAMQDispensateurClass.Doctor): string {
        if (partialRamqNumber == null)
            return "";

        if (partialRamqNumber.length < 5)
            return partialRamqNumber; // invalid

        if (partialRamqNumber.length >= 7)
            return partialRamqNumber; // invalid

        let first6DigitOfRamqNumber = "";
        if (partialRamqNumber.length == 6)
            first6DigitOfRamqNumber = partialRamqNumber; // hopefully the first 6

        if (partialRamqNumber.length == 5)
            first6DigitOfRamqNumber = dispensateurClass + partialRamqNumber;

        // calculate a checksum for a 6 digit number
        let total = 0;
        let subTotal = 0;
        let result = 0;

        for (let i = 7; i-- > 1;) {
            subTotal = parseInt(first6DigitOfRamqNumber[i - 1]);

            if (i % 2 == 0)
                subTotal = subTotal * 2;

            if (subTotal > 9)
                subTotal = (subTotal % 10) + 1;

            total = total + subTotal;
        }

        if (10 - (total % 10) != 10) {
            result = 10 - (total % 10);
        }
        else {
            result = 0;
        }

        return first6DigitOfRamqNumber + result.toString();
    }

    private GetFormattedRamqIntevenor(): string {
        let delegateName = "";
        if (!IsNullOrWhiteSpace(this.RAMQInscription?.ResidentPermit)) {
            switch (this.RAMQInscription.ResidentClass) {
                case RAMQDispensateurClass.Resident:
                    const residentName = RAMQInfo.ResolveClinicianName(`R${this.RAMQInscription.ResidentPermit}`);
                    delegateName += `Res: ${!IsNullOrWhiteSpace(residentName) ? residentName : this.RAMQInscription.ResidentPermit}`;
                    break;

                case RAMQDispensateurClass.IPS:
                    const ipsRamqNumber = `${this.RAMQInscription.ResidentClass}${this.RAMQInscription.ResidentPermit}`;
                    const ipsName = RAMQInfo.ResolveClinicianNameByRamqNumber(ipsRamqNumber);
                    delegateName += `IPS: ${!IsNullOrWhiteSpace(ipsName) ? ipsName : ipsRamqNumber}`;
                    break;
            }
        }
        else if (!IsNullOrWhiteSpace(this.RamqGl6Info?.DelegateProfessionnal?.LicenceType)) {
            const delegate = this.RamqGl6Info?.DelegateProfessionnal;
            switch (this.RamqGl6Info?.DelegateProfessionnal.LicenceType) {
                case "5":
                    delegateName += `Res: ${delegate.LastName}, ${delegate.FirstName}`;
                    break;

                case "8":
                    delegateName += `IPS: ${delegate.LastName}, ${delegate.FirstName}`;
                    break;
            }
        }

        let ramqProviderName = this.getRamqProviderName();
        if (!IsNullOrWhiteSpace(delegateName)) {
            // show delegate first now
            if (cacheStore.ClinicSettings.EnableRamqGL6Service)
                return `${delegateName} | ${ramqProviderName}`;

            return `${ramqProviderName} / ${delegateName}`;
        }

        return ramqProviderName;
    }
}

export { RAMQInfo, RamqStatus };
